import React, { useEffect, useMemo, useState } from 'react';
import './VeiculosmModule.css';

interface IndicadoresVeiculos {
  totalVeiculos: number;
  semChaveReserva: number;
  aVencerLicenciamento: number;
  aVencerTacografo: number;
}

interface VeiculoResumo {
  placa: string;
  marca: string;
  modelo: string;
  tipo: string;
  licenciamento?: string;
  temChaveReserva?: boolean;
  localGuardada?: string;
  ultimaValidacaoChave?: string;
  status: string;
}

interface VeiculoSemChave {
  placa: string;
  responsavel: string;
  operacao: string;
  ultimaAtualizacao: string;
}

interface VeiculoLicenciamento {
  placa: string;
  renavam: string;
  vencimento: string;
  diasRestantes: number;
  responsavel: string;
}

interface VeiculoTacografo {
  placa: string;
  tacografo: string;
  vencimento: string;
  diasRestantes: number;
  oficina: string;
}

interface DetalhesVeiculos {
  totalVeiculos: VeiculoResumo[];
  semChaveReserva: VeiculoSemChave[];
  aVencerLicenciamento: VeiculoLicenciamento[];
  aVencerTacografo: VeiculoTacografo[];
}

interface LinhaModalChaveReserva {
  placa: string;
  modelo: string;
  tipo: string;
  temChaveReserva: boolean;
  localGuardada: string;
}

interface PagamentoLicenciamento {
  pago: boolean;
  dataPagamento: string;
}

const INDICADORES_INICIAIS: IndicadoresVeiculos = {
  totalVeiculos: 0,
  semChaveReserva: 0,
  aVencerLicenciamento: 0,
  aVencerTacografo: 0
};

const DETALHES_INICIAIS: DetalhesVeiculos = {
  totalVeiculos: [],
  semChaveReserva: [],
  aVencerLicenciamento: [],
  aVencerTacografo: []
};

const VeiculosmModule: React.FC = () => {
  const PAGE_SIZE = 8;
  const [indicadores, setIndicadores] = useState<IndicadoresVeiculos>(INDICADORES_INICIAIS);
  const [detalhes, setDetalhes] = useState<DetalhesVeiculos>(DETALHES_INICIAIS);
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroMarca, setFiltroMarca] = useState<string>('');
  const [filtroModelo, setFiltroModelo] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<string>('');
  const [showChaveReservaModal, setShowChaveReservaModal] = useState<boolean>(false);
  const [linhasModalChaveReserva, setLinhasModalChaveReserva] = useState<LinhaModalChaveReserva[]>([]);
  const [buscaPlacaModal, setBuscaPlacaModal] = useState<string>('');
  const [savingChaveReserva, setSavingChaveReserva] = useState<boolean>(false);
  const [pagamentosLicenciamento, setPagamentosLicenciamento] = useState<Record<string, PagamentoLicenciamento>>({});
  const [showPagamentoLicenciamentoModal, setShowPagamentoLicenciamentoModal] = useState<boolean>(false);
  const [placaPagamentoSelecionada, setPlacaPagamentoSelecionada] = useState<string>('');
  const [dataPagamentoSelecionada, setDataPagamentoSelecionada] = useState<string>('');
  const [pageBySection, setPageBySection] = useState<Record<string, number>>({});

  const carregarDados = async () => {
    try {
      setLoading(true);
      setErro('');

      const response = await fetch('/api/veiculosm/detalhes');
      if (!response.ok) {
        throw new Error('Nao foi possivel carregar os dados do modulo.');
      }

      const data = await response.json();

      const detalhesData: DetalhesVeiculos = {
        totalVeiculos: Array.isArray(data?.totalVeiculos) ? data.totalVeiculos : [],
        semChaveReserva: Array.isArray(data?.semChaveReserva) ? data.semChaveReserva : [],
        aVencerLicenciamento: Array.isArray(data?.aVencerLicenciamento) ? data.aVencerLicenciamento : [],
        aVencerTacografo: Array.isArray(data?.aVencerTacografo) ? data.aVencerTacografo : []
      };

      setDetalhes(detalhesData);

      if (data?.indicadores) {
        setIndicadores({
          totalVeiculos: Number(data.indicadores.totalVeiculos || 0),
          semChaveReserva: Number(data.indicadores.semChaveReserva || 0),
          aVencerLicenciamento: Number(data.indicadores.aVencerLicenciamento || 0),
          aVencerTacografo: Number(data.indicadores.aVencerTacografo || 0)
        });
      } else {
        setIndicadores({
          totalVeiculos: detalhesData.totalVeiculos.length,
          semChaveReserva: detalhesData.semChaveReserva.length,
          aVencerLicenciamento: detalhesData.aVencerLicenciamento.length,
          aVencerTacografo: detalhesData.aVencerTacografo.length
        });
      }
    } catch {
      setErro('Nao foi possivel carregar os dados reais do modulo.');
      setIndicadores(INDICADORES_INICIAIS);
      setDetalhes(DETALHES_INICIAIS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    setPageBySection({});
  }, [filtroTipo, filtroMarca, filtroModelo]);

  const opcoesTipo = useMemo(() => {
    return Array.from(
      new Set(
        detalhes.totalVeiculos
          .map((item) => (item.tipo || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [detalhes.totalVeiculos]);

  const opcoesMarca = useMemo(() => {
    return Array.from(
      new Set(
        detalhes.totalVeiculos
          .map((item) => (item.marca || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [detalhes.totalVeiculos]);

  const opcoesModelo = useMemo(() => {
    return Array.from(
      new Set(
        detalhes.totalVeiculos
          .map((item) => (item.modelo || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [detalhes.totalVeiculos]);

  const placasFiltradas = useMemo(() => {
    const rows = detalhes.totalVeiculos.filter((item) => {
      const okTipo = !filtroTipo || item.tipo === filtroTipo;
      const okMarca = !filtroMarca || item.marca === filtroMarca;
      const okModelo = !filtroModelo || item.modelo === filtroModelo;

      return okTipo && okMarca && okModelo;
    });

    return new Set(rows.map((item) => item.placa));
  }, [detalhes.totalVeiculos, filtroTipo, filtroMarca, filtroModelo]);

  const detalhesFiltrados = useMemo<DetalhesVeiculos>(() => {
    return {
      totalVeiculos: detalhes.totalVeiculos.filter((item) => placasFiltradas.has(item.placa)),
      semChaveReserva: detalhes.semChaveReserva.filter((item) => placasFiltradas.has(item.placa)),
      aVencerLicenciamento: detalhes.aVencerLicenciamento.filter((item) => placasFiltradas.has(item.placa)),
      aVencerTacografo: detalhes.aVencerTacografo.filter((item) => placasFiltradas.has(item.placa))
    };
  }, [detalhes, placasFiltradas]);

  const indicadoresFiltrados = useMemo<IndicadoresVeiculos>(() => {
    return {
      totalVeiculos: detalhesFiltrados.totalVeiculos.length,
      semChaveReserva: detalhesFiltrados.semChaveReserva.length,
      aVencerLicenciamento: detalhesFiltrados.aVencerLicenciamento.length,
      aVencerTacografo: detalhesFiltrados.aVencerTacografo.length
    };
  }, [detalhesFiltrados]);

  const cards = useMemo(() => {
    return [
      { id: 'total', titulo: 'Total Veiculos', valor: indicadoresFiltrados.totalVeiculos, cor: 'azul' },
      { id: 'sem-chave', titulo: 'Sem Chave Reserva', valor: indicadoresFiltrados.semChaveReserva, cor: 'cinza' },
      { id: 'licenciamento', titulo: 'A Vencer Licenciamento', valor: indicadoresFiltrados.aVencerLicenciamento, cor: 'laranja' },
      { id: 'tacografo', titulo: 'A Vencer Tacografo', valor: indicadoresFiltrados.aVencerTacografo, cor: 'vermelho' }
    ];
  }, [indicadoresFiltrados]);

  const secoesTabela = useMemo(() => {
    return [
      {
        id: 'tabela-total-veiculos',
        titulo: 'Detalhe - Total Veiculos',
        colunas: ['Placa', 'Marca', 'Modelo', 'Tipo', 'Status'],
        linhas: detalhesFiltrados.totalVeiculos.map((item) => [item.placa, item.marca, item.modelo, item.tipo, item.status])
      },
      {
        id: 'tabela-sem-chave',
        titulo: 'Detalhe - Sem Chave Reserva',
        colunas: ['Placa', 'Ultima Atualizacao'],
        linhas: detalhesFiltrados.semChaveReserva.map((item) => [item.placa, item.ultimaAtualizacao])
      },
      {
        id: 'tabela-licenciamento',
        titulo: 'Detalhe - A Vencer Licenciamento',
        colunas: ['Placa', 'Renavam', 'Vencimento', 'Dias Restantes'],
        linhas: detalhesFiltrados.aVencerLicenciamento.map((item) => [item.placa, item.renavam || '-', item.vencimento, String(item.diasRestantes)])
      },
      {
        id: 'tabela-tacografo',
        titulo: 'Detalhe - A Vencer Tacografo',
        colunas: ['Placa', 'Tacografo', 'Vencimento', 'Dias Restantes'],
        linhas: detalhesFiltrados.aVencerTacografo.map((item) => [item.placa, item.tacografo || '-', item.vencimento || '-', String(item.diasRestantes)])
      }
    ];
  }, [detalhesFiltrados]);

  const abrirModalChaveReserva = () => {
    const linhas = detalhesFiltrados.totalVeiculos.map((veiculo) => {
      const linhaExistente = linhasModalChaveReserva.find((item) => item.placa === veiculo.placa);
      const localPadrao = (veiculo.localGuardada || '').toString();
      const temChavePadrao = Boolean(veiculo.temChaveReserva);

      return {
        placa: veiculo.placa,
        modelo: veiculo.modelo,
        tipo: veiculo.tipo,
        temChaveReserva: linhaExistente ? linhaExistente.temChaveReserva : temChavePadrao,
        localGuardada: linhaExistente ? linhaExistente.localGuardada : localPadrao
      };
    });

    setLinhasModalChaveReserva(linhas);
    setBuscaPlacaModal('');
    setShowChaveReservaModal(true);
  };

  const linhasModalFiltradas = useMemo(() => {
    const termo = buscaPlacaModal.trim().toUpperCase();
    if (!termo) {
      return linhasModalChaveReserva;
    }

    return linhasModalChaveReserva.filter((item) => item.placa.toUpperCase().includes(termo));
  }, [linhasModalChaveReserva, buscaPlacaModal]);

  const salvarChaveReserva = async () => {
    try {
      setSavingChaveReserva(true);

      const payload = {
        registros: linhasModalChaveReserva.map((item) => ({
          placa: item.placa,
          temChaveReserva: item.temChaveReserva,
          localGuardada: item.localGuardada
        }))
      };

      const response = await fetch('/api/veiculosm/chave-reserva', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Falha ao salvar chave reserva.');
      }

      setShowChaveReservaModal(false);
      await carregarDados();
    } catch {
      setErro('Nao foi possivel salvar os dados de chave reserva.');
    } finally {
      setSavingChaveReserva(false);
    }
  };

  const exportarTabelaCsv = (titulo: string, colunas: string[], linhas: string[][]) => {
    const esc = (valor: string) => `"${String(valor).replace(/"/g, '""')}"`;
    const cabecalho = colunas.map(esc).join(';');
    const corpo = linhas.map((linha) => linha.map(esc).join(';')).join('\n');
    const csv = [cabecalho, corpo].filter(Boolean).join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const nomeArquivo = `${titulo.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '')}.csv`;

    anchor.href = url;
    anchor.download = nomeArquivo;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  };

  const getPageForSection = (sectionId: string) => pageBySection[sectionId] || 1;

  const abrirModalPagamentoLicenciamento = (placa: string) => {
    const pagamentoAtual = pagamentosLicenciamento[placa];
    setPlacaPagamentoSelecionada(placa);
    setDataPagamentoSelecionada(pagamentoAtual?.dataPagamento || '');
    setShowPagamentoLicenciamentoModal(true);
  };

  const salvarPagamentoLicenciamento = () => {
    if (!placaPagamentoSelecionada) {
      return;
    }

    setPagamentosLicenciamento((prev) => ({
      ...prev,
      [placaPagamentoSelecionada]: {
        pago: true,
        dataPagamento: dataPagamentoSelecionada
      }
    }));

    setShowPagamentoLicenciamentoModal(false);
  };

  const setPageForSection = (sectionId: string, page: number) => {
    setPageBySection((prev) => ({
      ...prev,
      [sectionId]: Math.max(1, page)
    }));
  };

  return (
    <div className="veiculosm-module">
      <header className="veiculosm-header">
        <h1>Veículos</h1>
        <p>Indicadores da frota</p>
      </header>

      <section className="veiculosm-filtros" aria-label="Filtros de veiculos">
        <div className="veiculosm-filtro-item">
          <label htmlFor="filtro-tipo">Tipo</label>
          <select id="filtro-tipo" value={filtroTipo} onChange={(event) => setFiltroTipo(event.target.value)}>
            <option value="">Todos</option>
            {opcoesTipo.map((tipo) => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </select>
        </div>

        <div className="veiculosm-filtro-item">
          <label htmlFor="filtro-marca">Marca</label>
          <select id="filtro-marca" value={filtroMarca} onChange={(event) => setFiltroMarca(event.target.value)}>
            <option value="">Todas</option>
            {opcoesMarca.map((marca) => (
              <option key={marca} value={marca}>{marca}</option>
            ))}
          </select>
        </div>

        <div className="veiculosm-filtro-item">
          <label htmlFor="filtro-modelo">Modelo</label>
          <select id="filtro-modelo" value={filtroModelo} onChange={(event) => setFiltroModelo(event.target.value)}>
            <option value="">Todos</option>
            {opcoesModelo.map((modelo) => (
              <option key={modelo} value={modelo}>{modelo}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className="veiculosm-filtro-limpar"
          onClick={() => {
            setFiltroTipo('');
            setFiltroMarca('');
            setFiltroModelo('');
          }}
        >
          Limpar Filtros
        </button>
      </section>

      {erro && <div className="veiculosm-alert">{erro}</div>}

      <section className="veiculosm-cards-grid" aria-label="Indicadores de veiculos">
        {cards.map((card) => (
          <article key={card.id} className={`veiculosm-card veiculosm-card-${card.cor}`}>
            <h2>{card.titulo}</h2>
            <strong>{loading ? '...' : card.valor}</strong>
            {card.id === 'sem-chave' && (
              <button
                type="button"
                className="veiculosm-card-action"
                onClick={abrirModalChaveReserva}
              >
                Gerenciar Chave Reserva
              </button>
            )}
          </article>
        ))}
      </section>

      <section className="veiculosm-tables-grid" aria-label="Detalhamento por indicador">
        {secoesTabela.map((secao) => (
          <article key={secao.id} className="veiculosm-table-card">
            {(() => {
              const totalRows = secao.linhas.length;
              const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
              const currentPage = Math.min(getPageForSection(secao.id), totalPages);
              const start = (currentPage - 1) * PAGE_SIZE;
              const end = start + PAGE_SIZE;
              const pagedRows = secao.linhas.slice(start, end);
              const isLicenciamentoTable = secao.id === 'tabela-licenciamento';
              const totalColumns = secao.colunas.length + (isLicenciamentoTable ? 1 : 0);

              return (
                <>
                  <div className="veiculosm-table-header">
                    <h3>{secao.titulo}</h3>
                    <div className="veiculosm-table-actions">
                      <span>{totalRows} registro(s)</span>
                      <button
                        type="button"
                        className="veiculosm-table-export-btn"
                        onClick={() => exportarTabelaCsv(secao.titulo, secao.colunas, secao.linhas)}
                        disabled={loading}
                      >
                        Exportar
                      </button>
                    </div>
                  </div>
                  <div className="veiculosm-table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          {secao.colunas.map((coluna) => (
                            <th key={`${secao.id}-${coluna}`}>{coluna}</th>
                          ))}
                          {isLicenciamentoTable && <th>Pago</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={totalColumns} className="veiculosm-empty-row">Carregando...</td>
                          </tr>
                        ) : pagedRows.length > 0 ? (
                          pagedRows.map((linha, rowIndex) => (
                            <tr key={`${secao.id}-row-${start + rowIndex}`}>
                              {linha.map((valor, cellIndex) => (
                                <td key={`${secao.id}-row-${start + rowIndex}-cell-${cellIndex}`}>{valor}</td>
                              ))}
                              {isLicenciamentoTable && (
                                <td>
                                  <button
                                    type="button"
                                    className={`veiculosm-paid-btn ${pagamentosLicenciamento[linha[0]]?.pago ? 'is-paid' : 'is-unpaid'}`}
                                    onClick={() => abrirModalPagamentoLicenciamento(linha[0])}
                                  >
                                    {pagamentosLicenciamento[linha[0]]?.pago ? 'Pago' : 'Nao Pago'}
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={totalColumns} className="veiculosm-empty-row">Nenhum dado encontrado.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {!loading && totalRows > 0 && (
                    <div className="veiculosm-pagination">
                      <button
                        type="button"
                        className="veiculosm-pagination-btn"
                        onClick={() => setPageForSection(secao.id, currentPage - 1)}
                        disabled={currentPage <= 1}
                      >
                        Anterior
                      </button>
                      <span className="veiculosm-pagination-info">
                        Pagina {currentPage} de {totalPages}
                      </span>
                      <button
                        type="button"
                        className="veiculosm-pagination-btn"
                        onClick={() => setPageForSection(secao.id, currentPage + 1)}
                        disabled={currentPage >= totalPages}
                      >
                        Proxima
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </article>
        ))}
      </section>

      {showPagamentoLicenciamentoModal && (
        <div className="veiculosm-modal-overlay" role="dialog" aria-modal="true" aria-label="Informar pagamento de licenciamento">
          <div className="veiculosm-modal veiculosm-payment-modal">
            <div className="veiculosm-modal-header">
              <h3>Pagamento Licenciamento - {placaPagamentoSelecionada}</h3>
              <button
                type="button"
                onClick={() => setShowPagamentoLicenciamentoModal(false)}
              >
                Fechar
              </button>
            </div>

            <div className="veiculosm-modal-body veiculosm-payment-modal-body">
              <div className="veiculosm-payment-field">
                <label htmlFor="data-pagamento-licenciamento">Data de Pagamento</label>
                <input
                  id="data-pagamento-licenciamento"
                  type="date"
                  value={dataPagamentoSelecionada}
                  onChange={(event) => setDataPagamentoSelecionada(event.target.value)}
                />
              </div>
            </div>

            <div className="veiculosm-modal-footer">
              <button
                type="button"
                className="veiculosm-btn veiculosm-btn-muted"
                onClick={() => setShowPagamentoLicenciamentoModal(false)}
              >
                Cancelar
              </button>
              <button type="button" className="veiculosm-btn veiculosm-btn-primary" onClick={salvarPagamentoLicenciamento}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {showChaveReservaModal && (
        <div className="veiculosm-modal-overlay" role="dialog" aria-modal="true" aria-label="Gerenciar chave reserva">
          <div className="veiculosm-modal">
            <div className="veiculosm-modal-header">
              <h3>Chave Reserva por Veiculo</h3>
              <button
                type="button"
                onClick={() => {
                  setShowChaveReservaModal(false);
                  setBuscaPlacaModal('');
                }}
              >
                Fechar
              </button>
            </div>

            <div className="veiculosm-modal-body">
              <div className="veiculosm-modal-search">
                <input
                  type="text"
                  value={buscaPlacaModal}
                  placeholder="Pesquisar placa..."
                  onChange={(event) => setBuscaPlacaModal(event.target.value)}
                />
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Placa</th>
                    <th>Modelo</th>
                    <th>Tipo</th>
                    <th>Tem Chave Reserva</th>
                    <th>Onde Esta Guardada</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasModalFiltradas.length > 0 ? (
                    linhasModalFiltradas.map((linha) => (
                      <tr key={`modal-chave-${linha.placa}`}>
                        <td>{linha.placa}</td>
                        <td>{linha.modelo}</td>
                        <td>{linha.tipo}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={linha.temChaveReserva}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setLinhasModalChaveReserva((prev) => prev.map((item) => (
                                item.placa === linha.placa
                                  ? { ...item, temChaveReserva: checked }
                                  : item
                              )));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={linha.localGuardada}
                            placeholder="Ex.: Armario da Operacao"
                            onChange={(event) => {
                              const valor = event.target.value;
                              setLinhasModalChaveReserva((prev) => prev.map((item) => (
                                item.placa === linha.placa
                                  ? { ...item, localGuardada: valor }
                                  : item
                              )));
                            }}
                            disabled={!linha.temChaveReserva}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="veiculosm-empty-row">Nenhuma placa encontrada para o filtro informado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="veiculosm-modal-footer">
              <button type="button" className="veiculosm-btn veiculosm-btn-muted" onClick={() => setShowChaveReservaModal(false)}>
                Cancelar
              </button>
              <button type="button" className="veiculosm-btn veiculosm-btn-primary" onClick={salvarChaveReserva} disabled={savingChaveReserva}>
                {savingChaveReserva ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VeiculosmModule;
